import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Backdrop,
  Button,
  ButtonBase,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  Modal,
  TextField,
  Typography,
  Tooltip,
  FormHelperText,
  Link
} from "@mui/material";

import {
  getData,
  newPath,
  postData
} from "../../../components/utilities/UtilityFunctions";
import { postNotification } from "../../../components/utilities/Notifications";
import ModalLoadingFull from "../../../components/modals/ModalLoadingFull";
import ArmyPicker from "../../../components/utilities/ArmyPicker";

import ArmyListComponent from "./ArmyListComponent";
import { useConfirm } from "material-ui-confirm";
import * as Yup from "yup";
import { Formik } from "formik";
import moment from "moment";
import "moment-timezone";
import {
  getArmyPickerArmies,
  handlePagingAll
} from "../../../components/api/APIUtilities";
import GenericPicker from "../../../components/utilities/GenericPicker";
import ListResponsive from "../../../components/other/ListResponsive";
import { Box } from "@mui/system";
import {
  BCPArrowLeft,
  BCPArrowUpArrowDown,
  BCPCirclePlus,
  BCPCircleXmark,
  BCPPen,
  BCPRotateRight,
  BCPSearch
} from "../../../components/icons/bcp/BCPIcons";
import { getEnvironmentVariable } from "../../../global/environmentVariables";
import DeckSelection from "./SubComponents/DeckSelectionComponent";
import { isAppMode } from "../../../global/AppOverlay";
const IS_UY = getEnvironmentVariable("IS_UY") === "true";
const COMPANY_FLAG = getEnvironmentVariable("COMPANY_FLAG");
const appMode = isAppMode();

export default function EventPlayerDetails(props) {
  const { activeTab, setActiveTab, event, setEvent, authContext } = props;
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { t } = useTranslation("translation", {
    keyPrefix: "components.OrganizePlayerModal"
  });
  const { t: tRoster } = useTranslation("translation", {
    keyPrefix: "components.EventRoster"
  });
  const { t: tInfo } = useTranslation("translation", {
    keyPrefix: "components.EventInfo"
  });
  const { t: tArmyList } = useTranslation("translation", {
    keyPrefix: "components.ArmyListComponent"
  });

  const [passwordUpdated, setPasswordUpdated] = useState(false);

  const [player, setPlayer] = useState(null);
  const [armies, setArmies] = useState(null);
  const [armyDict, setArmyDict] = useState({});
  const [armyName, setArmyName] = useState(event?.player?.armyName);
  const [characters, setCharacters] = useState(null);

  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const [teams, setTeams] = useState(null);

  const [armyPickerOpen, setArmyPickerOpen] = useState(false);

  const handleSetPlayer = (newPlayer) => {
    setPlayer({
      ...player,
      ...newPlayer
    });
    setEvent({
      ...event,
      player: {
        ...event.player,
        ...newPlayer
      }
    });
  };

  const handleSetTeams = async (player) => {
    try {
      if (!player?.userId) {
        setTeams(null);
        return;
      }

      const apiTeams = await getData(`/users/${player?.userId}/teams`);

      let newTeams = [];
      if (apiTeams && apiTeams?.length) {
        apiTeams.sort((a, b) => {
          let teamA = a?.name?.toLowerCase();
          let teamB = b?.name?.toLowerCase();

          if (teamA > teamB) return 1;
          if (teamA < teamB) return -1;

          return 0;
        });

        apiTeams.forEach((team) =>
          newTeams.push({ label: team?.name, value: team?.id })
        );

        setTeams(newTeams);
      } else {
        setTeams(null);
      }
    } catch (err) {}
  };

  useEffect(() => {
    const check = async () => {
      try {
        if (!event?.player) {
          postNotification("warning", t("notActivePlayer"));
          var path = newPath({
            active_tab: "overview"
          });
          setActiveTab(0);
          navigate(path, { replace: false });
          return;
        }

        if (event.player.listId || event.player.armyListObjectId) {
          let armyList = await getData(
            `/armylists/${event.player.listId || event.player.armyListObjectId}`,
            {
              queryStringParameters: {
                "expand[]": ["army", "subFaction", "character"]
              }
            }
          );
          if (armyList) {
            event.player.armyList = armyList;
          }
        }

        setPlayer(event.player);

        if (event.gameSystem?.hasFactions) {
          const { armyArray, subFactionDict } = await getArmyPickerArmies(
            event?.gameSystem?.id || event?.gameSystemId
          );
          if (armyArray?.length) {
            setArmies(armyArray);
            setArmyDict(subFactionDict);
          }
        } else {
          setArmies(null);
        }
      } catch (err) {
        postNotification("error", err);
      }
      setLoading(false);
    };
    check();
    // eslint-disable-next-line
  }, [activeTab, event]);

  useEffect(() => {
    const check = async () => {
      if (!armyName) {
        setCharacters([]);
        return;
      }

      var apiCharacters = await handlePagingAll("/cards", {
        gameSystemId: event?.gameSystem?.id || event?.gameSystemId,
        faction: armyName,
        cardType: COMPANY_FLAG === "shard-bugs" ? "Cosmic Avatar" : "Character"
      });
      setCharacters(
        apiCharacters.map((el) => {
          return {
            value: el.id,
            label: el.name
          };
        })
      );
    };

    if (!event?.gameSystem?.supportsCharacters) {
      return;
    }

    setCharacters(null);
    check();

    // eslint-disable-next-line
  }, [armyName]);

  useEffect(() => {
    if (player) {
      const go = async () => {
        try {
          await handleSetTeams(player);
        } catch (err) {}
      };

      go();
    }
  }, [player]);

  return (
    <>
      <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
        <Grid container sx={{ gap: 2 }}>
          {!loading && (
            <>
              <Grid
                item
                xs={12}
                sx={{
                  p: 2
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h4" component="h2">
                      {t("playerStatus")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      disabled
                      value={
                        !player
                          ? ""
                          : player.dropped
                            ? tRoster("dropped")
                            : player.checkedIn
                              ? tRoster("checkedIn")
                              : tRoster("notCheckedIn")
                      }
                      label={tInfo("status")}
                      fullWidth
                    />
                  </Grid>
                  {!event.flags?.disableCheckin && (
                    <Grid item xs={6} sx={{ display: "flex" }}>
                      <Button
                        variant="contained"
                        fullWidth
                        color={
                          event.flags?.listsAtCheckin &&
                          !(player?.listId || player?.armyListObjectId)
                            ? "warning"
                            : "success"
                        }
                        disabled={
                          event.status?.ended ||
                          !player ||
                          !Boolean(
                            !player.dropped &&
                              !player.checkedIn &&
                              !event.status?.started &&
                              // || !event.currentRound
                              moment().isAfter(
                                moment(event.dates?.start).subtract(1, "day")
                              )
                          )
                        }
                        onClick={async () => {
                          try {
                            var errors = 0;

                            if (
                              event?.gameSystem?.hasFactions &&
                              !(player.factionId || player.armyId)
                            ) {
                              postNotification(
                                "error",
                                t("pleaseSelectFaction", {
                                  factionLabel:
                                    event?.gameSystem?.factionLabel ||
                                    t("faction")
                                })
                              );
                              errors++;
                            }
                            if (
                              event?.gameSystem?.subFactionRequired &&
                              !player.subFactionId
                            ) {
                              postNotification(
                                "error",
                                t("pleaseSelectSubFaction", {
                                  subFactionLabel:
                                    event?.gameSystem?.subFactionLabel ||
                                    t("subFaction")
                                })
                              );
                              errors++;
                            }
                            if (
                              event?.gameSystem?.supportsCharacters &&
                              !player.characterId
                            ) {
                              postNotification(
                                "error",
                                t("pleaseSelectCharacter", {
                                  characterLabel:
                                    event?.gameSystem?.characterLabel ||
                                    t("character")
                                })
                              );
                              errors++;
                            }

                            if (
                              event.flags?.listsAtCheckin &&
                              !(player?.listId || player?.armyListObjectId)
                            ) {
                              postNotification(
                                "error",
                                t("listRequiredAtCheckin", {
                                  listLabel:
                                    event?.gameSystem?.listLabel ||
                                    tArmyList("fallbacks.list")
                                })
                              );
                              errors++;
                            }
                            if (errors > 0) {
                              return;
                            }

                            setProcessing(true);

                            var updatePlayer = await postData(
                              `/players/${player?.id}`,
                              {
                                body: {
                                  set: { checkedIn: true }
                                }
                              }
                            );
                            handleSetPlayer(updatePlayer);
                          } catch (err) {
                            postNotification("error", err);
                          }

                          setProcessing(false);
                        }}
                      >
                        {t("checkIn")}
                      </Button>
                    </Grid>
                  )}

                  <Grid
                    item
                    xs={event.flags?.disableCheckin ? 12 : 6}
                    sx={{ display: "flex" }}
                  >
                    <Button
                      variant="contained"
                      fullWidth
                      color="error"
                      disabled={
                        event.status?.ended ||
                        !player ||
                        player.dropped ||
                        armyPickerOpen
                      }
                      onClick={async (e) => {
                        try {
                          await confirm({
                            title: t("confirmDrop"),
                            description: t("confirmDropDescription"),
                            dialogProps: {
                              sx: {
                                zIndex: 99999
                              }
                            }
                          });
                        } catch (err) {
                          return;
                        }

                        try {
                          setProcessing(true);

                          var updatePlayer = await postData(
                            `/players/${player?.id}`,
                            {
                              body: {
                                set: {
                                  dropped: true
                                }
                              }
                            }
                          );
                          handleSetPlayer(updatePlayer);
                        } catch (err) {
                          postNotification("error", err);
                        }

                        setProcessing(false);
                      }}
                    >
                      {t("drop")}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  p: 2
                }}
              >
                <Formik
                  initialValues={{
                    firstName:
                      player?.firstName || player?.user?.firstName || "",
                    lastName: player?.lastName || player?.user?.lastName || "",
                    teamId: player?.teamId || player?.team?.id || "",
                    factionId: player?.factionId || "",
                    characterId: player?.characterId || "",
                    subFactionId: player?.subFactionId || "",
                    systemId: player?.systemId || "",
                    pin: player?.hasPin ? "****" : ""
                  }}
                  enableReinitialize
                  validationSchema={Yup.object().shape({
                    firstName: Yup.string().max(128).required("Required"),
                    lastName: Yup.string().max(128).required("Required"),
                    pin: Yup.string().max(255)
                  })}
                  onSubmit={async (values) => {
                    if (!player) {
                      return;
                    }
                    try {
                      var error = false;
                      var sanitizedValues = {
                        set: {},
                        unset: {}
                      };

                      if (values.teamId !== "") {
                        sanitizedValues.set.teamId = values.teamId;
                      } else {
                        sanitizedValues.unset.teamId = true;
                        sanitizedValues.unset.teamName = true;
                      }
                      if (values.systemId !== "") {
                        sanitizedValues.set.systemId = values.systemId;
                      } else {
                        sanitizedValues.unset.systemId = true;
                      }

                      if (values.factionId !== "") {
                        sanitizedValues.set.armyId = values.factionId;
                      } else {
                        sanitizedValues.unset.armyId = true;
                        sanitizedValues.unset.armyName = true;
                      }

                      if (values.characterId !== "") {
                        sanitizedValues.set.characterId = values.characterId;
                      } else {
                        sanitizedValues.unset.characterId = true;
                        sanitizedValues.unset.characterName = true;
                      }

                      if (values.subFactionId !== "") {
                        sanitizedValues.set.subFactionId = values.subFactionId;
                      } else {
                        sanitizedValues.unset.subFactionId = true;
                        sanitizedValues.unset.subFactionName = true;
                      }

                      if (values.pin !== "" && passwordUpdated) {
                        sanitizedValues.set.pin = values.pin;
                      }

                      if (values.firstName) {
                        sanitizedValues.set.firstName = values.firstName;
                      }
                      if (values.lastName) {
                        sanitizedValues.set.lastName = values.lastName;
                      }

                      if (error) {
                        return;
                      }
                      setProcessing(true);
                      var updatePlayer = await postData(
                        `/players/${player.id}`,
                        {
                          body: sanitizedValues
                        }
                      );
                      handleSetPlayer(updatePlayer);
                      postNotification(
                        "success",
                        t("notifications.success.playerDetailsUpdated")
                      );
                    } catch (err) {
                      postNotification("error", err);
                    }
                    setProcessing(false);
                  }}
                >
                  {({
                    errors,
                    handleBlur,
                    handleChange,
                    handleSubmit,
                    touched,
                    values,
                    setFieldValue
                  }) => (
                    <form onSubmit={handleSubmit}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant="h4" component="h2">
                            {t("playerDetails")}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Tooltip
                            title={t("nameChangeTooltip")}
                            placement="top-end"
                          >
                            <TextField
                              name="firstName"
                              label={t("firstName")}
                              value={values.firstName}
                              variant="outlined"
                              fullWidth
                              required
                              margin="normal"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              error={Boolean(
                                touched.firstName && errors.firstName
                              )}
                              helperText={touched.firstName && errors.firstName}
                            />
                          </Tooltip>
                        </Grid>
                        <Grid item xs={12}>
                          <Tooltip
                            title={t("nameChangeTooltip")}
                            placement="top-end"
                          >
                            <TextField
                              name="lastName"
                              label={t("lastName")}
                              value={values.lastName}
                              variant="outlined"
                              fullWidth
                              required
                              margin="normal"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              error={Boolean(
                                touched.lastName && errors.lastName
                              )}
                              helperText={touched.lastName && errors.lastName}
                            />
                          </Tooltip>
                        </Grid>
                        {!IS_UY && (
                          <Grid item xs={12}>
                            <GenericPicker
                              name="teamId"
                              label={t("clubTeamName")}
                              value={values.teamId}
                              variant="outlined"
                              fullWidth
                              onBlur={handleBlur}
                              handleChange={handleChange}
                              error={Boolean(touched.teamId && errors.teamId)}
                              helperText={touched.teamId && errors.teamId}
                              list={teams || []}
                              inclAny={false}
                              clearable
                              endAdornment={
                                <InputAdornment position="end">
                                  <Button
                                    sx={{ minWidth: "40px" }}
                                    onClick={async (e) => {
                                      setProcessing(true);
                                      await handleSetTeams(player);
                                      setProcessing(false);
                                    }}
                                  >
                                    <BCPRotateRight />
                                  </Button>
                                </InputAdornment>
                              }
                            />
                            <FormHelperText>
                              {t("clubTeamHelpText")}{" "}
                              {appMode ? (
                                t("accountSettings")
                              ) : (
                                <Link
                                  href="/account?active_tab=teams"
                                  target="_blank"
                                >
                                  {t("account")}
                                </Link>
                              )}
                            </FormHelperText>
                          </Grid>
                        )}

                        {(event?.gameSystem?.useSystemId ||
                          event?.gameSystem?.systemIdRequired) && (
                          <Grid item xs={12}>
                            <TextField
                              name="systemId"
                              label={
                                event?.gameSystem?.systemIdName || t("systemId")
                              }
                              value={values.systemId}
                              variant="outlined"
                              fullWidth
                              required={event?.gameSystem?.systemIdRequired}
                              margin="normal"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              error={Boolean(
                                touched.systemId && errors.systemId
                              )}
                              helperText={touched.systemId && errors.systemId}
                            />
                          </Grid>
                        )}

                        {true || !event?.gameSystem?.supportsDeckBuilder ? (
                          <>
                            {event?.gameSystem?.hasFactions && (
                              <>
                                <Grid item xs={12}>
                                  <ArmyPicker
                                    armies={armies}
                                    name="factionId"
                                    label={
                                      event?.gameSystem?.factionLabel ||
                                      t("faction")
                                    }
                                    value={values.factionId}
                                    handleChange={(e) => {
                                      handleChange(e);
                                      let faction = armies.filter(
                                        (el) => el.id === e.target.value
                                      )[0];
                                      setArmyName(faction?.name);
                                      setFieldValue("subFactionId", "");
                                      setFieldValue("characterId", "");
                                      setArmyPickerOpen(false);
                                    }}
                                    onFocus={() => {
                                      setArmyPickerOpen(true);
                                    }}
                                    onBlur={() => {
                                      setArmyPickerOpen(false);
                                    }}
                                    disabled={player?.armyList?.armyListContent} // handle deck builder soft launch
                                    error={Boolean(
                                      touched.factionId && errors.factionId
                                    )}
                                    helperText={
                                      touched.factionId && errors.factionId
                                    }
                                    clearable
                                  />
                                </Grid>

                                {event?.gameSystem?.subFactionRequired && (
                                  <Grid item xs={12}>
                                    <GenericPicker
                                      name="subFactionId"
                                      label={
                                        event?.gameSystem?.subFactionLabel ||
                                        t("subFaction")
                                      }
                                      value={values.subFactionId}
                                      handleChange={handleChange}
                                      inclAny={false}
                                      clearable
                                      list={armyDict?.[values.factionId] || []}
                                      error={Boolean(
                                        touched.subFactionId &&
                                          errors.subFactionId
                                      )}
                                      helperText={
                                        touched.subFactionId &&
                                        errors.subFactionId
                                      }
                                      disabled={
                                        !values.factionId ||
                                        values.factionId === ""
                                      }
                                    />
                                  </Grid>
                                )}
                              </>
                            )}
                            {event?.gameSystem?.supportsCharacters && (
                              <Grid item xs={12}>
                                {!player?.armyList?.armyListContent &&
                                characters ? (
                                  <GenericPicker
                                    name="characterId"
                                    label={
                                      event?.gameSystem?.characterLabel ||
                                      t("character")
                                    }
                                    value={values.characterId}
                                    handleChange={handleChange}
                                    inclAny={false}
                                    clearable
                                    list={characters || []}
                                    error={Boolean(
                                      touched.characterId && errors.characterId
                                    )}
                                    helperText={
                                      touched.characterId && errors.characterId
                                    }
                                    disabled={player?.armyList?.armyListContent} // handle deck builder soft launch
                                  />
                                ) : (
                                  <TextField
                                    disabled
                                    label={
                                      event?.gameSystem?.characterLabel ||
                                      t("character")
                                    }
                                    value={
                                      player?.character?.name ||
                                      player?.characterName ||
                                      ""
                                    }
                                    color="primary"
                                    variant="outlined"
                                    fullWidth
                                    InputProps={
                                      !player?.armyList?.armyListContent
                                        ? {
                                            endAdornment: (
                                              <InputAdornment position="end">
                                                <CircularProgress />
                                              </InputAdornment>
                                            )
                                          }
                                        : null
                                    }
                                  />
                                )}
                              </Grid>
                            )}
                          </>
                        ) : (
                          <></>
                        )}
                        {event.flags?.enablePasswords &&
                          !event.flags?.passwordlessScoring && (
                            <Grid item xs={12}>
                              <TextField
                                name="pin"
                                label={t("selfScorePin")}
                                value={values.pin}
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                onBlur={(e) => {
                                  handleBlur(e);
                                }}
                                onChange={(e) => {
                                  handleChange(e);
                                  setPasswordUpdated(true);
                                }}
                                onFocus={(e) => {
                                  setFieldValue("pin", "");
                                }}
                                error={Boolean(touched.pin && errors.pin)}
                                helperText={touched.pin && errors.pin}
                                type="password"
                              />
                            </Grid>
                          )}
                        <Grid item xs={12}>
                          <Button
                            color="success"
                            fullWidth
                            variant="contained"
                            type="submit"
                          >
                            {t("update")}
                          </Button>
                        </Grid>
                      </Grid>
                    </form>
                  )}
                </Formik>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  p: 2
                }}
              >
                {COMPANY_FLAG === "solforge" ? (
                  <SolforgeDeckSelection
                    authContext={authContext}
                    event={event}
                    player={player}
                    setProcessing={setProcessing}
                    handleSetPlayer={handleSetPlayer}
                    t={t}
                  />
                ) : COMPANY_FLAG === "shard-bugs" ? (
                  <>
                    <DeckSelection
                      authContext={authContext}
                      event={event}
                      player={player}
                      processing={processing}
                      setProcessing={setProcessing}
                      handleSetPlayer={handleSetPlayer}
                      label="New Deck Registration"
                    />

                    {/* <DefaultListInfo
                      event={event}
                      player={player}
                      handleSetPlayer={handleSetPlayer}
                      sx={{ mt: 2 }}
                      label={
                        authContext?.authState?.identity?.isAdmin === true ||
                        authContext?.authState?.identity?.isTester === true ||
                        IS_DEV
                          ? "Legacy Deck Registration"
                          : null
                      }
                      helperText={
                        authContext?.authState?.identity?.isAdmin === true ||
                        authContext?.authState?.identity?.isTester === true ||
                        IS_DEV
                          ? "During the transition period from text deck lists to the deck builder deck lists, we realize there may be some events in progress, or decks already submitted. As such we are allowing the text based deck lists for a small transition window."
                          : null
                      }
                      setProcessing={setProcessing}
                      armies={armies}
                      characters={characters}
                      setArmyName={setArmyName}
                    /> */}
                  </>
                ) : (
                  <DefaultListInfo
                    event={event}
                    player={player}
                    handleSetPlayer={handleSetPlayer}
                    t={t}
                    tArmyList={tArmyList}
                  />
                )}
              </Grid>
            </>
          )}
        </Grid>
      </Container>
      {(loading || processing) && <ModalLoadingFull />}
    </>
  );
}

const DefaultListInfo = (props) => {
  const {
    event,
    player,
    handleSetPlayer,
    sx,
    label,
    helperText,
    t,
    tArmyList
  } = props;
  const [armyModalOpen, setArmyModalOpen] = useState(false);
  return (
    <>
      <Grid container spacing={2} sx={sx}>
        <Grid item xs={12}>
          <Typography variant="h4" component="h2">
            {label ||
              t("listInformation", {
                listLabel:
                  event?.gameSystem?.listLabel || tArmyList("fallbacks.list")
              })}
          </Typography>
          {helperText && (
            <Typography variant="caption">{helperText}</Typography>
          )}
        </Grid>

        <Grid item xs={12}>
          <TextField
            disabled
            value={
              !player
                ? ""
                : !(player?.listId || player?.armyListObjectId)
                  ? t("noList", {
                      listLabel:
                        event?.gameSystem?.listLabel ||
                        tArmyList("fallbacks.list")
                    })
                  : player.listStatus === "pending"
                    ? t("pendingFormat")
                    : t("submitted")
            }
            label={t("listStatus", {
              listLabel:
                event?.gameSystem?.listLabel || tArmyList("fallbacks.list")
            })}
            fullWidth
          />
        </Grid>

        {player?.listId || player?.armyListObjectId ? (
          <>
            <Grid item xs={12} sm={6}>
              <Button
                color={!event.flags?.listsLocked ? "primary" : "warning"}
                variant="outlined"
                fullWidth
                disabled={
                  event.status?.ended ||
                  !player ||
                  player.dropped ||
                  player?.armyList?.armyListContent
                }
                onClick={() => {
                  if (!event.flags?.listsLocked) {
                    setArmyModalOpen(true);
                  } else {
                    postNotification(
                      "warning",
                      t("listsLockedNotification", {
                        listLabel:
                          event?.gameSystem?.listLabel ||
                          tArmyList("fallbacks.list")
                      })
                    );
                  }
                }}
              >
                {t("editList", {
                  listLabel:
                    event?.gameSystem?.listLabel || tArmyList("fallbacks.list")
                })}
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                color="primary"
                variant="outlined"
                fullWidth
                href={
                  player.listUrl !== undefined && player.listUrl !== ""
                    ? player.listUrl
                    : `/${
                        player?.armyList?.armyListContent ? "deck" : "list"
                      }/${player?.listId || player?.armyListObjectId}`
                }
                target="_blank"
              >
                {t("viewList", {
                  listLabel:
                    event?.gameSystem?.listLabel || tArmyList("fallbacks.list")
                })}
              </Button>
            </Grid>
          </>
        ) : (
          <Grid item xs={12}>
            <Button
              color="primary"
              variant="outlined"
              fullWidth
              disabled={
                event.status?.ended ||
                !player ||
                player.dropped ||
                player?.armyList?.armyListContent
              }
              onClick={() => {
                setArmyModalOpen(true);
              }}
            >
              {t("addList", {
                listLabel:
                  event?.gameSystem?.listLabel || tArmyList("fallbacks.list")
              })}
            </Button>
          </Grid>
        )}
      </Grid>

      {player && (
        <>
          <ArmyListComponent
            modal
            event={event}
            open={armyModalOpen}
            setOpen={setArmyModalOpen}
            player={player}
            setPlayer={handleSetPlayer}
            active
            tArmyList={tArmyList}
          />
        </>
      )}
    </>
  );
};

const SolforgeDeckSelection = (props) => {
  const { authContext, event, player, setProcessing, handleSetPlayer, t } =
    props;
  const [open, setOpen] = useState(false);

  const initialValues = {
    forgeborn: player?.armyListSolforge?.forgeborn || "",
    fusedDeck: player?.armyListSolforge || null
  };
  delete initialValues.fusedDeck?.forgeborn;

  const validationSchema = {
    forgeborn: Yup.string()
      .min(3, "Too Short")
      .max(255, "Too Long")
      .required("Required")
  };

  const [fusedDecks, setFusedDecks] = useState(null);
  const [fuseOpen, setFuseOpen] = useState(false);
  const fetchFusedDecks = async (lastEvaluatedKey) => {
    const queryStringParameters = {
      pageSize: 200,
      username: authContext?.authState?.identity?.username,
      deckRank: event?.format?.eventSubType
    };
    if (lastEvaluatedKey) {
      queryStringParameters.exclusiveStartKey =
        JSON.stringify(lastEvaluatedKey);
    }
    var apiGetData = await getData(
      `/fuseddeck`,
      {
        queryStringParameters: queryStringParameters
      },
      "SolforgeBearer"
    );

    if (apiGetData.LastEvaluatedKey) {
      var nextFetch = await fetchDecks(apiGetData.LastEvaluatedKey);
      return [...apiGetData.Items, ...nextFetch];
    } else {
      return apiGetData.Items || [];
    }
  };
  const resetFusedDecks = async () => {
    var apiDecks = await fetchFusedDecks();
    setFusedDecks(apiDecks.filter((deck) => !deck.isArchived));
  };

  const [decks, setDecks] = useState(null);
  const fetchDecks = async (lastEvaluatedKey) => {
    const queryStringParameters = {
      pageSize: 200,
      username: authContext?.authState?.identity?.username,
      deckRank: event?.format?.eventSubType
    };
    if (lastEvaluatedKey) {
      queryStringParameters.exclusiveStartKey =
        JSON.stringify(lastEvaluatedKey);
    }

    var apiGetData = await getData(
      `/deck`,
      {
        queryStringParameters: queryStringParameters
      },
      "SolforgeBearer"
    );

    if (apiGetData.LastEvaluatedKey) {
      var nextFetch = await fetchDecks(apiGetData.LastEvaluatedKey);
      return [...apiGetData.Items, ...nextFetch];
    } else {
      return apiGetData.Items || [];
    }
  };
  const resetDecks = async () => {
    var apiDecks = await fetchDecks();
    setDecks(apiDecks);
  };

  useEffect(() => {
    const go = async () => {
      await resetFusedDecks();
      await resetDecks();
    };
    go();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={Yup.object().shape(validationSchema)}
        onSubmit={async (values) => {
          try {
            if (!values.fusedDeck) {
              postNotification(
                "error",
                t("notifications.errors.pleaseFuseDeck")
              );
              return;
            }
            setProcessing(true);
            var body = {
              playerId: player.id,
              listInfo: {
                fusedDeck: {
                  id: values.fusedDeck?.id,
                  name: values.fusedDeck?.name,
                  forgeborn: values.forgeborn,
                  myDecks: values.fusedDeck?.myDecks?.map((el) => {
                    return {
                      id: el.id,
                      name: el.name,
                      faction: el.faction,
                      forgeborn: {
                        title: el.forgeborn?.title || ""
                      },
                      cards: el.cards || {}
                    };
                  })
                }
              }
            };
            var listText = `${values.fusedDeck?.name || "Fused Deck"}`;
            var deckNo = 0;
            for (let deck of values.fusedDeck.myDecks) {
              if (deck.forgeborn?.title === values.forgeborn) {
                body.listInfo.fusedDeck.faction = deck.faction;
              }
              deckNo++;
              var creatures = Object.values(deck.cards || {})
                .filter((el) => el.cardType === "Creature")
                .map((el) => el.title)
                .join("\n");
              var spells = Object.values(deck.cards || {})
                .filter((el) => el.cardType === "Spell")
                .map((el) => el.title)
                .join("\n");
              listText += `\n\nDeck ${deckNo}: ${deck.name}\nForgeborn: ${
                deck.forgeborn?.title || "Unknown"
              }${deck.forgeborn?.title === values.forgeborn ? "*" : ""}${
                creatures !== "" ? `\n\nCreatures:\n${creatures}` : ""
              }${spells !== "" ? `\n\nCreatures:\n${spells}` : ""}`;
            }
            body.listInfo.listText = listText;
            try {
              var apiData = await postData(`/armylists`, {
                body: body
              });
              if (apiData?.id && handleSetPlayer) {
                handleSetPlayer({
                  ...player,
                  listId: apiData.id,
                  armyListObjectId: apiData.id,
                  armyListSolforge: apiData.armyListSolforge,
                  listStatus: apiData.listStatus || player.listStatus
                });
              }
              postNotification(
                "success",
                `${event?.gameSystem?.listLabel || "List"} update successful`
              );
            } catch (err) {
              postNotification("error", err);
            }
            setProcessing(false);
          } catch (error) {
            postNotification("error", error.message || JSON.stringify(error));
            setProcessing(false);
          }
        }}
      >
        {({
          errors,
          handleChange,
          handleSubmit,
          touched,
          values,
          setFieldValue
        }) => (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h4" component="h2">
                  Deck Registration
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <ButtonBase
                  onClick={() => {
                    setOpen(true);
                  }}
                  sx={{
                    width: "100%",
                    p: 2,
                    border: "1px solid rgba(0, 0, 0, 0.12)",
                    borderRadius: 1,
                    boxShadow: 1
                  }}
                >
                  {values.fusedDeck ? (
                    <Grid
                      container
                      spacing={1}
                      textAlign="left"
                      alignItems="center"
                    >
                      <Grid item>
                        <Box
                          sx={{
                            position: "relative",
                            backgroundColor: "#FFFFFF",
                            width: "60px",
                            pb: "60px",
                            borderRadius: "4px",
                            overflow: "hidden"
                          }}
                        >
                          {[0, 1].map((deckNo) => {
                            if (!values.fusedDeck?.myDecks?.[deckNo]) {
                              return <></>;
                            }

                            return (
                              <Box
                                sx={{
                                  backgroundColor: "#FFFFFF",
                                  backgroundSize: "cover",
                                  width: "50%",
                                  pb: "100%",
                                  position: "absolute",
                                  [deckNo === 0 ? "left" : "right"]: 0,
                                  display: "flex"
                                }}
                              >
                                <Box
                                  sx={{
                                    background: `url(${getEnvironmentVariable(
                                      "HEADER_SOLFORGE_URL"
                                    )}/images/cards/forgeborn/${values.fusedDeck?.myDecks?.[
                                      deckNo
                                    ].forgeborn.title
                                      .split(" ")
                                      .join("-")
                                      .toLowerCase()}.jpg)`,
                                    backgroundSize: "cover",
                                    backgroundPosition: "70%, center",
                                    width: "100%",
                                    height: "100%",
                                    position: "absolute",
                                    display: "flex"
                                  }}
                                />
                              </Box>
                            );
                          })}
                        </Box>
                      </Grid>
                      <Grid item sx={{ flex: 1 }}>
                        <Typography variant="body1">
                          {values.fusedDeck.name}
                        </Typography>
                        <Typography variant="caption">
                          {values.fusedDeck.myDecks
                            .map((deck) => deck.name)
                            .join(" - ")}
                        </Typography>
                      </Grid>
                      <Grid item>
                        <BCPArrowUpArrowDown />
                      </Grid>
                    </Grid>
                  ) : (
                    <Typography
                      gutterBottom
                      variant="h5"
                      component="h3"
                      color="primary"
                    >
                      <BCPCirclePlus
                        color="inherit"
                        sx={{ fontSize: "inherit" }}
                      />{" "}
                      Fuse Deck
                    </Typography>
                  )}
                </ButtonBase>
              </Grid>
              <Grid item xs={12}>
                {values.fusedDeck?.myDecks?.length ? (
                  <GenericPicker
                    name="forgeborn"
                    label={"Forgeborn"}
                    value={values.forgeborn}
                    handleChange={handleChange}
                    inclAny={false}
                    required
                    list={
                      values.fusedDeck?.myDecks?.length > 0
                        ? values.fusedDeck.myDecks.map(
                            (deck) => deck.forgeborn?.title
                          )
                        : []
                    }
                    error={Boolean(touched.forgeborn && errors.forgeborn)}
                    helperText={touched.forgeborn && errors.forgeborn}
                  />
                ) : (
                  <TextField
                    label={"Forgeborn"}
                    value={"Please fuse your deck"}
                    variant="outlined"
                    fullWidth
                    multiline
                    required
                    margin="normal"
                    disabled={true}
                  />
                )}
              </Grid>
              <Grid item xs={12}>
                <Button
                  color="success"
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={values.forgeborn === "" || !values.fusedDeck}
                >
                  Update
                </Button>
              </Grid>
            </Grid>
            <Modal
              disableAutoFocus
              open={open}
              onClose={() => {
                setOpen(false);
              }}
              closeAfterTransition
              BackdropComponent={Backdrop}
              BackdropProps={{
                timeout: 500
              }}
              sx={{ zIndex: 99990 }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: ["95vw", "85vw", 600],
                  maxHeight: "95vh",

                  bgcolor: "background.paper",
                  border: "1px solid rgba(0, 0, 0, 0.12)",
                  borderRadius: 1,
                  boxShadow: 24
                }}
              >
                <Card sx={{ p: 1 }}>
                  <CardHeader
                    title="Fuse New Deck"
                    sx={{ pb: 0 }}
                    action={
                      <IconButton
                        aria-label="close"
                        onClick={() => {
                          setOpen(false);
                        }}
                        size="large"
                      >
                        <BCPCircleXmark />
                      </IconButton>
                    }
                  />
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={async (e) => {
                          await resetFusedDecks();
                          await resetDecks();
                        }}
                        startIcon={<BCPRotateRight />}
                      >
                        Refresh List
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        variant="outlined"
                        color="primary"
                        fullWidth
                        onClick={(e) => {
                          setFuseOpen(true);
                        }}
                        startIcon={<BCPCirclePlus />}
                      >
                        Fuse New Deck
                      </Button>
                    </Grid>
                    <Grid item xs={12} sx={{ minHeight: "50vh" }}>
                      <ListResponsive
                        originalList={fusedDecks}
                        doSearch
                        sx={{
                          maxHeight: "50vh",
                          overflowY: "auto",
                          pr: 1
                        }}
                        searchProps={{
                          label: "Search Decks",
                          performSearch: (originalList, searchText) => {
                            if (!searchText || searchText === "") {
                              return originalList;
                            } else {
                              var filteredList = originalList.filter(
                                function (el) {
                                  if (
                                    el?.name?.toLowerCase().includes(searchText)
                                  )
                                    return true;

                                  return false;
                                }
                              );
                              return filteredList;
                            }
                          }
                        }}
                        rowProps={{
                          mapFunction: (row) => (
                            <Grid
                              item
                              xs={12}
                              onClick={() => {
                                var sortedFusedDecks = [...fusedDecks];
                                sortedFusedDecks.sort((a, b) => {
                                  if (a.id === row.id) return -1;
                                  if (b.id === row.id) return 1;
                                  return 0;
                                });
                                setFusedDecks(sortedFusedDecks);
                                setFieldValue("fusedDeck", row);
                                setOpen(false);
                              }}
                              sx={{
                                p: 1,
                                mb: 1,
                                border: "1px solid rgba(0, 0, 0, 0.12)",
                                borderRadius: 1,
                                boxShadow: 1,
                                cursor: "pointer",

                                borderColor:
                                  row.id === values.fusedDeck?.id
                                    ? "primary.main"
                                    : null,
                                borderWidth:
                                  row.id === values.fusedDeck?.id ? "2px" : null
                              }}
                            >
                              <Grid container spacing={1}>
                                <Grid item>
                                  <Box
                                    sx={{
                                      position: "relative",
                                      backgroundColor: "#FFFFFF",
                                      width: "60px",
                                      pb: "60px",
                                      borderRadius: "4px",
                                      overflow: "hidden"
                                    }}
                                  >
                                    {[0, 1].map((deckNo) => {
                                      if (!row?.myDecks?.[deckNo]) {
                                        return <></>;
                                      }

                                      return (
                                        <Box
                                          sx={{
                                            backgroundColor: "#FFFFFF",
                                            backgroundSize: "cover",
                                            width: "50%",
                                            pb: "100%",
                                            position: "absolute",
                                            [deckNo === 0 ? "left" : "right"]:
                                              0,
                                            display: "flex"
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              background: `url(${getEnvironmentVariable(
                                                "HEADER_SOLFORGE_URL"
                                              )}/images/cards/forgeborn/${row?.myDecks?.[
                                                deckNo
                                              ].forgeborn.title
                                                .split(" ")
                                                .join("-")
                                                .toLowerCase()}.jpg)`,
                                              backgroundSize: "cover",
                                              backgroundPosition: "70%, center",
                                              width: "100%",
                                              height: "100%",
                                              position: "absolute",
                                              display: "flex"
                                            }}
                                          />
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                </Grid>
                                <Grid item>
                                  <Typography variant="body1">
                                    {row.name}
                                  </Typography>
                                  <Typography variant="caption">
                                    {row.myDecks
                                      .map((deck) => deck.name)
                                      .join(" - ")}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Grid>
                          )
                        }}
                      />
                    </Grid>
                  </Grid>
                </Card>
              </Box>
            </Modal>
          </form>
        )}
      </Formik>

      <SolforgeFuseDeckModal
        halfDecks={decks}
        decks={fusedDecks}
        setDecks={setFusedDecks}
        setParentOpen={setOpen}
        open={fuseOpen}
        setOpen={setFuseOpen}
        setProcessing={setProcessing}
        authContext={authContext}
      />
    </>
  );
};

const SolforgeFuseDeckModal = (props) => {
  const {
    halfDecks,
    decks,
    setDecks,
    authContext,
    open,
    setOpen,
    setProcessing,
    setParentOpen
  } = props;

  const initialValues = {
    name: "",
    deck1: null,
    deck2: null
  };
  const validationSchema = {
    name: Yup.string()
      .min(3, "Too Short")
      .max(255, "Too Long")
      .required("Required")
  };

  const [searchText, setSearchText] = useState("");

  const [subOpen, setSubOpen] = useState(false);
  const [deckNo, setDeckNo] = useState(0);

  return (
    <Modal
      disableAutoFocus
      open={open}
      onClose={() => {
        setOpen(false);
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500
      }}
      sx={{ zIndex: 99990 }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: ["95vw", "85vw", 600],
          maxHeight: "95vh",

          bgcolor: "background.paper",
          border: "1px solid rgba(0, 0, 0, 0.12)",
          borderRadius: 1,
          boxShadow: 24
        }}
      >
        <Card>
          <CardHeader
            title="Fuse New Deck"
            sx={{ pb: 0 }}
            action={
              <IconButton
                aria-label="close"
                onClick={() => {
                  setOpen(false);
                }}
                size="large"
              >
                <BCPCircleXmark />
              </IconButton>
            }
          />
          <Formik
            initialValues={initialValues}
            enableReinitialize={true}
            validationSchema={Yup.object().shape(validationSchema)}
            onSubmit={async (values, { setFieldValue }) => {
              try {
                setProcessing(true);

                if (authContext?.authState?.identity?.username) {
                  if (!values.deck1) {
                    postNotification(
                      "error",
                      "Please select a deck for Deck 1"
                    );
                  }
                  if (!values.deck2) {
                    postNotification(
                      "error",
                      "Please select a deck for Deck 2"
                    );
                  }
                  if (values.deck1 && values.deck2) {
                    if (values.deck1.faction === values.deck2.faction) {
                      postNotification(
                        "error",
                        "Fused Decks cannot contain 2 Decks with the same Faction"
                      );
                    } else {
                      var ok = true;
                      decks.forEach((deck) => {
                        if (
                          values.name.toLowerCase() === deck.name.toLowerCase()
                        ) {
                          ok = false;
                          postNotification(
                            "error",
                            "You already have a Fused Deck with this name."
                          );
                        }
                        var hasDeck1 = false;
                        var hasDeck2 = false;
                        deck.myDecks.forEach((halfDeck) => {
                          if (halfDeck.id === values.deck1.id) {
                            hasDeck1 = true;
                          }
                          if (halfDeck.id === values.deck2.id) {
                            hasDeck2 = true;
                          }
                        });
                        if (hasDeck1 && hasDeck2) {
                          ok = false;
                          postNotification(
                            "error",
                            `You already have this Deck combination in Fused Deck: ${deck.name}.`
                          );
                        }
                      });
                      if (ok) {
                        var postBody = {
                          username: authContext?.authState?.identity?.username,
                          name: values.name,
                          myDecks: [
                            {
                              id: values.deck1.id,
                              name: values.deck1.name,
                              faction: values.deck1.faction,
                              forgeborn: {
                                title: values.deck1.forgeborn.title
                              }
                            },
                            {
                              id: values.deck2.id,
                              name: values.deck2.name,
                              faction: values.deck2.faction,
                              forgeborn: {
                                title: values.deck2.forgeborn.title
                              }
                            }
                          ]
                        };

                        var apiPostData = await postData(
                          "/fuseddeck",
                          {
                            body: postBody
                          },
                          "SolforgeBearer"
                        );

                        if (apiPostData.error) {
                          postNotification("error", apiPostData.error);
                        } else {
                          var allItems = decks;
                          allItems.unshift(apiPostData.data);
                          setDecks(allItems);
                          setOpen(false);
                          setParentOpen(false);
                          postNotification(
                            "success",
                            "Success! Your deck has been created."
                          );
                        }
                      }
                    }
                  }
                } else {
                  postNotification(
                    "error",
                    "You are not logged in, please login and try again."
                  );
                }

                setProcessing(false);
              } catch (error) {
                postNotification(
                  "error",
                  error.message || JSON.stringify(error)
                );

                setProcessing(false);
              }
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              touched,
              values,
              setFieldValue
            }) => (
              <form onSubmit={handleSubmit}>
                <CardContent sx={{ flexGrow: 1, pt: 0 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Name your deck"
                        margin="normal"
                        name="name"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.name}
                        color="secondary"
                        variant="outlined"
                        required
                        error={Boolean(touched.name && errors.name)}
                        helperText={touched.name && errors.name}
                      />
                    </Grid>

                    {[1, 2].map((deckNo) => {
                      return (
                        <Grid item xs={12}>
                          <ButtonBase
                            onClick={(e) => {
                              setDeckNo(deckNo);
                              setSubOpen(true);
                            }}
                            sx={{
                              textAlign: "left",
                              width: "100%",
                              border: "1px solid rgba(0, 0, 0, 0.12)",
                              borderRadius: "4px",
                              p: 1
                            }}
                          >
                            {values[`deck${deckNo}`] && (
                              <Grid container spacing={1} alignItems="center">
                                <Grid item>
                                  <Avatar
                                    sx={{ borderRadius: "4px" }}
                                    src={`${getEnvironmentVariable(
                                      "HEADER_SOLFORGE_URL"
                                    )}/images/cards/forgeborn/${values[
                                      `deck${deckNo}`
                                    ].forgeborn.title
                                      .split(" ")
                                      .join("-")
                                      .toLowerCase()}.jpg`}
                                  />
                                </Grid>
                                <Grid item sx={{ flex: 1 }}>
                                  <Typography
                                    gutterBottom
                                    variant="h6"
                                    component="h3"
                                  >
                                    {values[`deck${deckNo}`].name}
                                  </Typography>
                                </Grid>
                                <Grid item>
                                  <BCPPen color="primary" />
                                </Grid>
                              </Grid>
                            )}
                            {!values[`deck${deckNo}`] && (
                              <Box sx={{ width: "100%", pt: 1 }}>
                                <Typography
                                  gutterBottom
                                  variant="h6"
                                  component="h3"
                                  color="primary"
                                >
                                  <BCPCirclePlus
                                    color="inherit"
                                    sx={{ fontSize: "inherit" }}
                                  />{" "}
                                  Add Deck {deckNo}
                                </Typography>
                              </Box>
                            )}
                          </ButtonBase>
                        </Grid>
                      );
                    })}
                  </Grid>
                </CardContent>
                <CardActions
                  sx={{
                    pt: 1,
                    pr: 2,
                    pb: 2,
                    display: "block",
                    textAlign: "right"
                  }}
                >
                  <Button
                    onClick={() => setOpen(false)}
                    color="primary"
                    variant="outlined"
                  >
                    Cancel
                  </Button>
                  <Button type="submit" color="primary" variant="contained">
                    Save
                  </Button>
                </CardActions>
                <Modal
                  disableAutoFocus
                  open={subOpen}
                  onClose={() => {
                    setSubOpen(false);
                  }}
                  closeAfterTransition
                  BackdropComponent={Backdrop}
                  BackdropProps={{
                    timeout: 500
                  }}
                  sx={{ zIndex: 99990 }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: ["95vw", "85vw", 600],
                      maxHeight: "95vh",

                      bgcolor: "background.paper",
                      border: "1px solid rgba(0, 0, 0, 0.12)",
                      borderRadius: 1,
                      boxShadow: 24
                    }}
                  >
                    <Card>
                      <CardHeader
                        avatar={
                          <IconButton
                            aria-label="close"
                            onClick={() => {
                              setSubOpen(false);
                            }}
                            size="large"
                          >
                            <BCPArrowLeft />
                          </IconButton>
                        }
                        title="Select Deck"
                        action={
                          <IconButton
                            aria-label="close"
                            onClick={() => {
                              setSubOpen(false);
                              setOpen(false);
                            }}
                            size="large"
                          >
                            <BCPCircleXmark />
                          </IconButton>
                        }
                      />

                      <CardContent>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <BCPSearch />
                                  </InputAdornment>
                                )
                              }}
                              placeholder="Search for a deck"
                              helperText="Search by name, faction or forgeborn"
                              variant="outlined"
                              size="small"
                              value={searchText}
                              onChange={(e) => {
                                setSearchText(e.target.value.toLowerCase());
                              }}
                            />
                          </Grid>
                        </Grid>
                        <Box
                          sx={{
                            height: "60vh",
                            pr: 1,
                            overflowY: "scroll"
                          }}
                        >
                          <Grid container spacing={2}>
                            {halfDecks &&
                              halfDecks
                                .filter((deck) => {
                                  if (!searchText || searchText.trim() === "") {
                                    return true;
                                  }

                                  var searchFields = searchText.split(" ");
                                  for (let searchField of searchFields) {
                                    var found = false;
                                    if (
                                      deck?.name
                                        ?.toLowerCase()
                                        .includes(searchField.toLowerCase()) ||
                                      deck?.faction
                                        ?.toLowerCase()
                                        .includes(searchField.toLowerCase()) ||
                                      deck?.forgeborn?.title
                                        ?.toLowerCase()
                                        .includes(searchField.toLowerCase())
                                    ) {
                                      found = true;
                                    }
                                    if (!found) return false;
                                  }
                                  return true;
                                })
                                .map((deck) => {
                                  return (
                                    <Grid item key={deck.id} xs={12}>
                                      <ButtonBase
                                        sx={{
                                          width: "100%",
                                          textAlign: "left",
                                          border:
                                            "1px solid rgba(0, 0, 0, 0.12)",
                                          borderRadius: 1,
                                          p: 1
                                        }}
                                        onClick={(e) => {
                                          setFieldValue("deck" + deckNo, deck);
                                          setDeckNo(0);
                                          setSubOpen(false);
                                        }}
                                      >
                                        <Grid
                                          container
                                          spacing={1}
                                          alignItems="center"
                                        >
                                          <Grid item>
                                            <Avatar
                                              sx={{ borderRadius: "4px" }}
                                              src={`${getEnvironmentVariable(
                                                "HEADER_SOLFORGE_URL"
                                              )}/images/cards/forgeborn/${deck.forgeborn.title
                                                .split(" ")
                                                .join("-")
                                                .toLowerCase()}.jpg`}
                                            />
                                          </Grid>
                                          <Grid item sx={{ flex: 1 }}>
                                            <Typography
                                              gutterBottom
                                              variant="h6"
                                              component="h3"
                                            >
                                              {deck.name}
                                            </Typography>
                                          </Grid>
                                          <Grid item>
                                            <BCPCirclePlus color="primary" />
                                          </Grid>
                                        </Grid>
                                      </ButtonBase>
                                    </Grid>
                                  );
                                })}

                            {!halfDecks && (
                              <Grid item xs={12}>
                                <Box
                                  sx={{
                                    width: "100%",
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    marginBottom: "22%"
                                  }}
                                >
                                  <CircularProgress size={100} />
                                </Box>
                              </Grid>
                            )}

                            {halfDecks && halfDecks.length === 0 && (
                              <Grid item xs={12} sm={6} md={4}>
                                <Typography
                                  gutterBottom
                                  variant="h5"
                                  component="h2"
                                >
                                  No decks found
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                </Modal>
              </form>
            )}
          </Formik>
        </Card>
      </Box>
    </Modal>
  );
};
